import { on, jq, parsePrice } from '../core/dom.js';
import { money } from '../core/money.js';

/**
 * The classic funnel (spec §1): view_content → begin_checkout → add_shipping_info →
 * add_payment_info → purchase.
 *
 * add_to_cart is deliberately absent here — it is sent from PHP, where an in-app
 * WebView cannot lose it.
 *
 * begin_checkout replaces initiate_checkout, which the spec retires: v1 fired it on
 * anything matching `body.woocommerce-checkout`, and landing one-pagers embed the
 * checkout shortcode, so 71% of all visitors triggered it. Here it is bound to the
 * server-classified page type instead of to markup.
 */

export function collectCommerce(tracker, { win = window, doc = document, data = {}, selectors = {} } = {}) {
  const pageType = tracker.context.page_type;

  if (pageType === 'product') {
    collectViewContent(tracker, data.viewContent);
  }

  if (pageType === 'checkout') {
    collectBeginCheckout(tracker, data.cart);
    collectShippingAndPayment(tracker, doc, selectors);
  }

  if (pageType === 'order_received') {
    collectPurchase(tracker, data.purchase);
  }

  // Checkout only, like the two branches above. Bound unconditionally it also ran on
  // landing one-pagers, where collectLanding already listens to `checkout_error` — one
  // refusal then produced two browser reports of the same failure.
  if (pageType === 'checkout') {
    collectCheckoutPaymentError(tracker, win, doc);
  }
}

export function collectViewContent(tracker, product) {
  if (!product || !product.product_sku) {
    return;
  }

  tracker.track(
    'view_content',
    {
      product_sku: product.product_sku,
      product_id: product.product_id,
      product_name: product.product_name,
      price: money(product.price),
      currency: product.currency,
      category: (product.categories && product.categories[0]) || undefined,
    },
    { once: 'session', dedupeKey: 'view_content:' + product.product_sku }
  );
}

/**
 * Once per session, on the real checkout page only.
 */
export function collectBeginCheckout(tracker, cart) {
  if (!cart) {
    return;
  }

  tracker.track(
    'begin_checkout',
    {
      cart_value: money(cart.cart_value !== undefined ? cart.cart_value : cart.value),
      item_count: cart.item_count,
      currency: cart.currency,
      items: cart.items,
    },
    { once: 'session', dedupeKey: 'begin_checkout' }
  );
}

export function collectShippingAndPayment(tracker, doc = document, selectors = {}) {
  const shippingSent = {};

  const shippingSelector =
    selectors.shippingMethod || 'input[name^="shipping_method"], select[name^="shipping_method"]';
  const paymentSelector = selectors.paymentMethod || 'input[name="payment_method"]';

  on(doc, 'change', shippingSelector, (event, el) => {
    const method = String(el.value || '');

    if (!method || shippingSent[method]) {
      return;
    }
    shippingSent[method] = true;

    tracker.track('add_shipping_info', {
      shipping_method: method,
      shipping_cost: money(shippingCost(el)),
    });
  });

  const paymentSent = {};

  on(doc, 'change', paymentSelector, (event, el) => {
    const method = String(el.value || '');

    if (!method || paymentSent[method]) {
      return;
    }
    paymentSent[method] = true;

    tracker.track('add_payment_info', { payment_method: method });
  });
}

/**
 * Reads the shipping cost out of the rate label — WooCommerce renders it there and
 * nowhere machine-readable. The rendered amount element is preferred over the whole
 * label, which often carries a delivery time ("Kurier 24h") ahead of the price.
 */
function shippingCost(input) {
  const label =
    (input.id && input.ownerDocument.querySelector('label[for="' + input.id + '"]')) ||
    input.closest('li');

  if (!label) {
    return undefined;
  }

  const amount = label.querySelector('.woocommerce-Price-amount, .amount');

  return parsePrice(amount ? amount.textContent : label.textContent);
}

/**
 * Persistent deduplication: the thank-you page is routinely reopened from the order
 * confirmation e-mail, sometimes days later.
 */
export function collectPurchase(tracker, order) {
  if (!order || !order.order_id) {
    return;
  }

  tracker.track(
    'purchase',
    {
      order_id: order.order_id,
      value: money(order.value),
      order_total: money(order.order_total),
      currency: order.currency,
      items: order.items,
      item_count: order.item_count,
      payment_method: order.payment_method,
      landing_id: order.landing_id || undefined,
      has_subscription: order.has_subscription,
    },
    // `shared`: PHP emits the same event with dedupe_key = purchase:{order_id}, so this
    // key must go out unscoped and byte-identical or the two copies cannot be collapsed.
    // It is the only browser event that opts out of session scoping.
    { once: 'persistent', dedupeKey: 'purchase:' + order.order_id, shared: true }
  );
}

/**
 * WooCommerce reports a refused payment by re-rendering the checkout with an error
 * notice and triggering `checkout_error`.
 *
 * This is deliberately NOT `payment_failed`. That event is owned by PHP, which keys it
 * `payment_failed:{order_id}:{attempt}` — an order id the browser does not have at this
 * point in the flow, so the two copies could never share a dedupe_key and every refusal
 * was counted twice. AGENTS.md's rule is to send only one of them; this is the
 * browser-only signal (the customer saw a refusal in the UI), under its own name so it
 * cannot be mistaken for the authoritative outcome.
 */
export function collectCheckoutPaymentError(tracker, win = window, doc = document) {
  const $ = jq(win);

  if (!$ || typeof $ !== 'function') {
    return;
  }

  $(doc.body).on('checkout_error', (_event, messages) => {
    const text = String(messages || errorText(doc));

    if (!looksLikePayment(text)) {
      return;
    }

    // The notice text itself is never forwarded. A WooCommerce notice repeats what the
    // customer typed, so it can carry an e-mail address or a full postal address —
    // assets/src/collectors/landing.js reads this same node and refuses it for the same
    // reason. `looksLikePayment` above is the only thing we take from the text.
    // No dedupeKey on purpose. This event has no server copy to collapse against, so the
    // envelope's per-emission default is right: two refusals in one session are two
    // distinct rows, and neither trips the duplicate_event_fired alert.
    tracker.track('checkout_payment_error', {
      payment_method: selectedPaymentMethod(doc),
      decline_reason: 'checkout_error',
    });
  });
}

function errorText(doc) {
  const notice = doc.querySelector('.woocommerce-error, .wc-block-components-notice-banner');

  return notice ? notice.textContent : '';
}

function selectedPaymentMethod(doc) {
  const checked = doc.querySelector('input[name="payment_method"]:checked');

  return checked ? String(checked.value) : '';
}

/**
 * A refusal in any of the store languages — PL, SI, DE, ES, IT — plus the gateway
 * names. Deliberately narrow: a missing postcode is a field error, not a payment one.
 */
function looksLikePayment(text) {
  return /(payment|card|declin|refus|gateway|stripe|paypal|klarna|mollie|plač|kartic|płatnoś|zahlung|kreditkart|karte|abgelehnt|pago|tarjeta|rechaz|deneg|pagament|carta|rifiut|respint)/i.test(
    text
  );
}
